<script setup lang="ts">
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { vMaska } from 'maska/vue'
import { Mask, type MaskaDetail, type MaskInputOptions } from 'maska'

import useAuthStore from '@/stores/authStore'

import { BANKGIRO_MASKS, PLUSGIRO_MASKS, normalizeGiro } from '@/utils/bankAccount'
import {
  bankgiroIssue,
  isPlausiblePlusgiroNumber,
  isValidBankgiroNumber,
  isValidNormalizedPlusgiroNumber,
} from '@/utils/validation'
import type { PayoutMethod } from './types'

import BankAccountFields from './BankAccountFields.vue'

// How the money should reach the debtor: the method choice plus the fields that method needs.
// Shared by the refund wizard's method page and the profile dialog (page 2, ADA-4345 step 2.15), so
// that "which methods exist" and "what counts as a valid number" are answered in one place —
// the two surfaces write to the same `payout_details` and must not disagree.
//
// Rules WARN but never block on empty, matching BankAccountFields: the profile page can be
// submitted untouched, and the wizard enforces presence through its own `canContinue`.
const method = defineModel<PayoutMethod>('method', { required: true })
const clearingNumber = defineModel<string>('clearingNumber', { required: true })
const accountNumber = defineModel<string>('accountNumber', { required: true })
const bankgiro = defineModel<string>('bankgiro', { required: true })
const plusgiro = defineModel<string>('plusgiro', { required: true })

// The wizard heads the fields with "fill in the details"; the profile page goes straight from the
// method choice to the fields. Passed in rather than branched on so neither surface shifts.
//
// `storedBankgiro`/`storedPlusgiro` are the giro numbers currently on the record, and they make
// the blocking rules change-aware (ADA-4350 review, extended to plusgiro in ADA-4349): the api's
// x_rule skips a value the PATCH does not change, so a number stored before the rule existed never
// blocks a later edit to something else. Surfaces that edit a record pass them; the refund wizard
// leaves them undefined — nothing is stored there, so every value is a new one.
//
// `hideHeadings`: the profile page heads the section itself, so it gets neither the method-choice
// line nor the bank-account intro; the dialog and the wizard keep both.
const props = defineProps<{
  detailsHeading?: string
  storedBankgiro?: string
  storedPlusgiro?: string
  hideHeadings?: boolean
}>()

const { t } = useI18n()
const authStore = useAuthStore()

// Bank transfer is hidden for companies (design + step 2.9): a company is paid to its giro.
const isCompany = computed(() => authStore.selectedToken?.payload?.org_nbr != null)

// Bankgiro is always available, so the list is never empty.
const methods = computed<{ value: PayoutMethod; label: string }[]>(() => [
  ...(isCompany.value ? [] : [{ value: 'bank_account' as const, label: t('common.bank_transfer') }]),
  { value: 'bankgiro', label: t('common.bankgiro') },
  { value: 'plusgiro', label: t('common.plusgiro') },
])

// A company session must never sit on a hidden bank-transfer selection, even when the stored
// payout details or an earlier private session carried one.
watch(
  methods,
  (available) => {
    if (!available.some((option) => option.value === method.value)) {
      method.value = available[0]?.value ?? 'bankgiro'
    }
  },
  { immediate: true }
)

const giro = computed({
  get: () => (method.value === 'bankgiro' ? bankgiro.value : plusgiro.value),
  set: (value: string) => {
    if (method.value === 'bankgiro') bankgiro.value = value
    else plusgiro.value = value
  },
})

// Separators are stripped as they arrive ("123-4567" → "1234567") so the shape rule and the
// api both see digits. The field is text + inputmode="numeric", not type="number" — a number
// input reports the separator form as EMPTY (badInput) and the value would never reach the
// model at all; inputmode still raises the numeric keyboard on mobile.
watch(giro, (value) => {
  const digits = value.replace(/\D/g, '')
  if (digits !== value) giro.value = digits
})

// Live giro mask: maska picks the mask by digit count (160-9908 vs 1609-9088), which Vuetify's
// single-mask VMaskInput cannot. The model stays digits-only: onMaska writes the digits on every keystroke.
const giroMasks = computed<readonly string[]>(() => (method.value === 'bankgiro' ? BANKGIRO_MASKS : PLUSGIRO_MASKS))
const giroMask = computed(() => new Mask({ mask: [...giroMasks.value] }))
const giroMaskaOptions = computed<MaskInputOptions>(() => ({
  mask: [...giroMasks.value],
  onMaska: (detail: MaskaDetail) => {
    giro.value = detail.unmasked
  },
}))
// The displayed text is derived from the model through the same Mask, so it always equals what maska
// just wrote into the input and a re-render never moves the caret mid-typing.
const giroDisplay = computed(() => giroMask.value.masked(giro.value))

// Whether a giro field still holds what the record already stores. Compared NORMALIZED on both
// sides, since that is the only form in which two spellings of one number are the same number.
//
// "Unchanged" requires a NON-EMPTY stored value: normalizing maps both an absent giro and an
// all-zero input to '', so without this a fresh '0000000' would match "nothing stored" and be
// waved through as untouched — green field, live save button, then a 422 or a silent no-op.
// It also keeps the wizard, which stores nothing and passes no prop, exempt from the exemption.
const bankgiroUnchanged = computed(() => {
  const stored = normalizeGiro(props.storedBankgiro ?? '')
  return stored !== '' && normalizeGiro(bankgiro.value) === stored
})
const plusgiroUnchanged = computed(() => {
  const stored = normalizeGiro(props.storedPlusgiro ?? '')
  return stored !== '' && normalizeGiro(plusgiro.value) === stored
})

function giroRule(value: string) {
  if (value === '') return true
  if (method.value === 'bankgiro') {
    // Judged on the NORMALIZED value, like the gate that decides whether the form may be
    // submitted (ADA-4350 review): '001609908' is a payable seven-digit bankgiro once the zeros
    // go, and a rule reading the raw value would mark the field red while the button stayed live.
    if (bankgiroUnchanged.value) return true
    return isValidBankgiroNumber(normalizeGiro(value)) || t('common.number_of_digits', ['7-8'])
  }
  // Plusgiro is judged on the normalized value (ADA-4349), unlike bankgiro above: it is the only
  // blocking rule this instrument has — there is no red alert behind it — so the field rule must
  // say what the continue/save gate is holding on. '00' and '007' look shape-valid but normalize
  // below the two-digit floor, and this is where the debtor is told so.
  if (plusgiroUnchanged.value) return true
  return isValidNormalizedPlusgiroNumber(value) || t('common.number_of_digits', ['2-8'])
}

// The specification line for bankgiro (ADA-4350 decision #1): Bankgirot's manual states mod 10
// on the last digit without reservation, so a number failing it cannot exist and the api rejects
// it with a 422 — blocking it here only moves the rejection to where the debtor can fix it.
// Shown lazily like the bank account fields (only once the field is shape-complete, never while
// typing), and the text ends with the contact-us path.
const giroBlockingMessage = computed(() => {
  if (method.value !== 'bankgiro') return undefined
  // Change-aware, mirroring the x_rule server-side: a stored number the debtor has not touched is
  // not theirs to fix from here, and flagging it would block edits to unrelated fields.
  if (bankgiroUnchanged.value) return undefined
  if (!isValidBankgiroNumber(normalizeGiro(bankgiro.value))) return undefined
  if (bankgiroIssue(bankgiro.value) === undefined) return undefined
  return t('common.invalid_bankgiro_number')
})

// Check digit, warning only — plusgiro alone, and permanently so (ADA-4349 decision #1): its
// specification hedges the rule ("som regel"), and a block is a hard stop with no override, so a
// genuine number the check cannot confirm would have no registration path at all. The api saves
// it and logs the failure. Bankgiro left this class entirely in ADA-4350.
// Gated on the form rule passing, so a number that is already being rejected for its length does
// not also carry a check-digit warning.
//
// Deliberately NOT change-aware, unlike the red alert and the two blocking rules above: those are
// exempted for a stored value because they would otherwise hold the debtor's unrelated edits
// hostage, and this one blocks nothing — it is information about the number in the field, the api
// logs the same failure whether or not the value is new, and hiding it for a stored number would
// mean the one debtor who could act on it never sees it.
const showGiroWarning = computed(() => {
  if (method.value !== 'plusgiro') return false
  if (!isValidNormalizedPlusgiroNumber(plusgiro.value)) return false
  return !isPlausiblePlusgiroNumber(plusgiro.value)
})

// The stored value is the normalized value (ADA-4350 decision #5): separators dropped by the
// watcher above, leading zeros dropped here on blur rather than while typing. Both instruments
// since ADA-4349 — writing through `giro` normalizes whichever one the method points at.
//
// Only a number that is valid FOR ITS INSTRUMENT is rewritten (ADA-4350 review): normalizing
// unconditionally would take '0000123' down to '123' behind the debtor's back while the field is
// telling them it is wrong, and the digits they typed are the ones they need to see to find their
// mistake. For plusgiro the bar is the form rule alone — its check digit only warns (ADA-4349
// decision #1), and a warning must not hold back the canonical spelling.
function normalizeGiroField() {
  if (method.value === 'bankgiro') {
    if (bankgiroIssue(bankgiro.value) !== undefined) return
  } else if (!isValidNormalizedPlusgiroNumber(plusgiro.value)) {
    return
  }
  giro.value = normalizeGiro(giro.value)
}
</script>

<template>
  <div
    v-if="!props.hideHeadings"
    class="pt-4 text-body-medium font-weight-bold"
  >
    {{ t('common.choose_payout_method') }}
  </div>

  <v-radio-group
    v-model="method"
    hide-details
  >
    <v-radio
      v-for="option in methods"
      :key="option.value"
      :label="option.label"
      :value="option.value"
    />
  </v-radio-group>

  <div
    v-if="props.detailsHeading != null"
    class="pt-4 text-body-medium font-weight-bold"
  >
    {{ props.detailsHeading }}
  </div>

  <!-- Bank transfer reuses the account fields, which derive the bank from the clearing number
       and warn on a failing check digit. -->
  <bank-account-fields
    v-if="method === 'bank_account'"
    v-model:clearing-number="clearingNumber"
    v-model:account-number="accountNumber"
    :hide-intro="props.hideHeadings"
  />

  <div
    v-else
    class="pt-6"
  >
    <v-text-field
      v-maska="giroMaskaOptions"
      :model-value="giroDisplay"
      :validation-value="giro"
      :label="t(`common.${method}`)"
      :rules="[giroRule]"
      inputmode="numeric"
      @blur="normalizeGiroField"
    />
    <v-alert
      v-if="giroBlockingMessage"
      :text="giroBlockingMessage"
      type="error"
      density="compact"
      variant="tonal"
      class="mt-2"
    />
    <v-alert
      v-if="showGiroWarning"
      :text="t('common.giro_check_digit_warning')"
      type="warning"
      density="compact"
      variant="tonal"
      class="mt-2"
    />
  </div>
</template>
